import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Copy } from "lucide-react";
import { isStandalone } from "@/lib/install";
import { claimHandoff, clearHandoff, isInAppBrowser, pendingHandoff, startGoogleSignIn } from "@/lib/googleAuth";
import { api, errText } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BrandHeader, Spinner } from "@/components/Brand";
import { SignInHelp } from "@/components/SignInHelp";
import { Mail } from "lucide-react";
import { Btn } from "@/components/Btn";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

export default function AuthPage() {
  const { signIn } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const isLogin = mode === "login";

  const [waiting, setWaiting] = useState(!!pendingHandoff() && isStandalone());
  const inApp = isInAppBrowser();

  useEffect(() => {
    if (!waiting) return;
    let stop = false;
    const check = async () => {
      const n = pendingHandoff();
      if (!n) return setWaiting(false);
      try {
        const data = await claimHandoff(n);
        if (data && !stop) signIn(data);
      } catch {}
    };
    check();
    const t = setInterval(check, 2500);
    const onVis = () => document.visibilityState === "visible" && check();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      stop = true;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [waiting, signIn]);

  const google = () => {
    startGoogleSignIn();
    if (pendingHandoff()) setWaiting(true);
  };
  const cancelWaiting = () => {
    clearHandoff();
    setWaiting(false);
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      toast.success("Link copied — paste it into Safari or Chrome");
    } catch {
      toast(window.location.origin);
    }
  };

  const [magicSent, setMagicSent] = useState(false);
  const sendMagic = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/magic-link", { email: form.email });
      setMagicSent(true);
    } catch (ex) {
      setErr(errText(ex));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post(isLogin ? "/auth/login" : "/auth/register", isLogin ? { email: form.email, password: form.password } : form);
      signIn(data);
    } catch (ex) {
      setErr(errText(ex));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:block relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1635776062360-af423602aff3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0F1722]/45" />
        <div className="absolute bottom-14 left-14 right-14 text-white tf-rise">
          <p className="text-sm uppercase tracking-[0.2em] opacity-80 mb-4">Regulate first. Reflect second.</p>
          <h2 className="text-4xl xl:text-5xl font-bold leading-tight max-w-lg">When panic hits, your calm is one tap away.</h2>
          <p className="mt-4 text-white/80 max-w-md">Somatic grounding and the TIP method, then gentle schema maps that turn hard moments into awareness — and a report for your psychologist.</p>
        </div>
      </div>
      <div className="flex flex-col px-6 sm:px-12 py-8 max-w-xl w-full mx-auto">
        <BrandHeader size={30} />
        <div className="my-auto py-10 tf-rise">
          <h1 className="text-4xl sm:text-5xl font-bold text-t-on" data-testid="auth-title">{mode === "register" ? "Create your space" : "Welcome back"}</h1>
          <p className="mt-3 text-t-muted">{mode === "register" ? "A private place for your check-ins, flips and reports." : mode === "magic" ? "We'll email you a one-tap sign-in link — no password needed." : "Sign in to keep your crisis tools one tap away."}</p>
          {inApp ? (
            <div className="mt-8 rounded-2xl border border-t-border bg-t-brand3 p-4 text-sm text-t-on" data-testid="inapp-browser-notice">
              <p className="font-semibold flex items-center gap-2"><ExternalLink size={16} className="text-t-brand" /> Open in your browser to use Google</p>
              <p className="mt-1 text-t-on2">Google doesn't allow sign-in inside apps like Messenger, Instagram or Facebook. Tap the ⋯ menu and choose <b>Open in browser</b> (or Safari / Chrome), or sign in with email below.</p>
              <button onClick={copyLink} className="mt-2 inline-flex items-center gap-1.5 font-semibold text-t-brand" data-testid="inapp-copy-link"><Copy size={15} /> Copy link</button>
            </div>
          ) : waiting ? (
            <div className="mt-8 rounded-2xl border border-t-brand bg-t-brand3 p-4 text-sm text-t-on" data-testid="google-waiting">
              <p className="font-semibold flex items-center gap-2"><Spinner className="w-4 h-4 text-t-brand" /> Finishing Google sign-in…</p>
              <p className="mt-1 text-t-on2">Complete sign-in in the window that opened, then close it (tap <b>Done</b>) to come back here — you'll be signed in automatically.</p>
              <div className="mt-2 flex gap-4">
                <button onClick={google} className="font-semibold text-t-brand" data-testid="google-retry">Open Google again</button>
                <button onClick={cancelWaiting} className="font-semibold text-t-muted" data-testid="google-cancel">Cancel</button>
              </div>
            </div>
          ) : (
            <Btn variant="outline" className="w-full mt-8" onClick={google} icon={<GoogleIcon />} label="Continue with Google" testId="google-login-button" />
          )}
          <div className="flex items-center gap-3 my-6 text-t-muted text-sm"><span className="flex-1 h-px bg-t-border" />or<span className="flex-1 h-px bg-t-border" /></div>
          {mode === "magic" ? (
            magicSent ? (
              <div className="tf-card p-5 text-center" data-testid="magic-sent">
                <Mail size={28} className="mx-auto text-t-brand" />
                <p className="mt-3 font-semibold text-t-on">Check your inbox</p>
                <p className="mt-1 text-sm text-t-on2">We've emailed a sign-in link to <b>{form.email}</b>. It works once and expires in 15 minutes. Check spam if you don't see it.</p>
                <button onClick={() => setMagicSent(false)} className="mt-3 text-sm font-semibold text-t-brand" data-testid="magic-resend">Use a different email or resend</button>
              </div>
            ) : (
              <form onSubmit={sendMagic} className="flex flex-col gap-3">
                <input className="tf-input" type="email" required placeholder="Email" value={form.email} onChange={set("email")} data-testid="magic-email-input" autoComplete="email" />
                {err ? <p className="text-sm font-medium" style={{ color: "var(--hot)" }} data-testid="auth-error">{err}</p> : null}
                <Btn type="submit" className="w-full mt-2" loading={busy} icon={<Mail size={17} />} label="Email me a sign-in link" testId="magic-submit-button" />
                <p className="text-xs text-t-muted text-center">No password needed — new here? The link creates your account.</p>
              </form>
            )
          ) : (
          <form onSubmit={submit} className="flex flex-col gap-3">
            {!isLogin ? <input className="tf-input" placeholder="Your name" value={form.name} onChange={set("name")} data-testid="auth-name-input" /> : null}
            <input className="tf-input" type="email" required placeholder="Email" value={form.email} onChange={set("email")} data-testid="auth-email-input" autoComplete="username" />
            <input className="tf-input" type="password" required minLength={6} placeholder="Password" value={form.password} onChange={set("password")} data-testid="auth-password-input" autoComplete={isLogin ? "current-password" : "new-password"} />
            {err ? <p className="text-sm font-medium" style={{ color: "var(--hot)" }} data-testid="auth-error">{err}</p> : null}
            <Btn type="submit" className="w-full mt-2" loading={busy} label={isLogin ? "Sign in" : "Create account"} testId="auth-submit-button" />
          </form>
          )}
          {mode !== "magic" ? (
            <Btn variant="secondary" className="w-full mt-3" icon={<Mail size={17} />} label="Email me a sign-in link instead" onClick={() => { setMode("magic"); setErr(""); }} testId="magic-mode-button" />
          ) : null}
          <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); setMagicSent(false); }} className="mt-6 w-full text-center text-sm text-t-brand font-medium" data-testid="auth-toggle-mode">
            {mode === "login" ? "New here? Create an account" : mode === "magic" ? "Sign in with a password instead" : "Already have an account? Sign in"}
          </button>
          <SignInHelp />
        </div>
        <p className="text-xs text-t-muted">Triggerific-Pilot is a self-help tool and not a crisis service. In an emergency, call your local emergency number.</p>
      </div>
    </div>
  );
}
